import { useRef, useEffect } from 'react';
import { createAudioBlob } from '../utils/audioUtils';
import { logMessage } from '../utils/logMessage';

export const useAudioPlayback = (
  contactName: string,
  onSpeaking: (who: string | null) => void,
  setBlockMic: (blocked: boolean) => void,
  setMicEnabled: (enabled: boolean) => void,
  isMuted: boolean,
  volume: number
) => {
  const contactNameRef = useRef(contactName);
  const onSpeakingRef = useRef(onSpeaking);
  const isMutedRef = useRef(isMuted);
  const volumeRef = useRef(volume);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  useEffect(() => { contactNameRef.current = contactName; }, [contactName]);
  useEffect(() => { onSpeakingRef.current = onSpeaking; }, [onSpeaking]);
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = isMuted;
  }, [isMuted]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
      setMicEnabled(true);
      onSpeakingRef.current(null);
    };
  }, []);

  const playAudio = (audioData: ArrayBuffer, onEnded?: () => void) => {
    if (isMutedRef.current) return;
    try {
      const audioBlob = createAudioBlob(audioData);
      const audioUrl = URL.createObjectURL(audioBlob);
      audioUrlRef.current = audioUrl;
      const audio = new Audio(audioUrl);

      audio.volume = volumeRef.current;
      audio.muted = isMutedRef.current;
      audioRef.current = audio;

      // --- Surface 1: MediaError events (network, decode, unsupported format, abort) ---
      // This fires independently of play() and covers loading/decoding-level failures.
      // Note: In some browsers this may fire alongside a play() rejection — both are
      // intentionally logged separately since they expose different failure dimensions.
      audio.onerror = () => {
        const mediaErrorCode = audio.error?.code;
        const mediaErrorMessage = audio.error?.message ?? 'unknown';

        logMessage('audio_element_error', 'error', {
          source: 'useAudioPlayback',
          mediaErrorCode,           // 1=aborted, 2=network, 3=decode, 4=src_not_supported
          mediaErrorMessage,
          contact: contactNameRef.current,
        });

        setBlockMic(false);
        onSpeakingRef.current(null);
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
          audioUrlRef.current = null;
        }
        audioRef.current = null;
        if (onEnded) onEnded();
      };

      setBlockMic(true);
      onSpeakingRef.current(contactNameRef.current);

      audio.onended = () => {
        setBlockMic(false);
        setMicEnabled(true);
        onSpeakingRef.current(null);
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
          audioUrlRef.current = null;
        }
        audioRef.current = null;
        if (onEnded) onEnded();
      };

      // --- Surface 2: play() promise rejection (autoplay policy, unsupported at play time) ---
      // NotAllowedError → browser autoplay policy blocked the call, logged as warn since
      // it is an environment/policy issue and not a code defect.
      // All other DOMExceptions are logged as error.
      audio.play().catch((err: DOMException) => {
        if (err.name === 'NotAllowedError') {
          logMessage('audio_autoplay_blocked', 'warn', {
            source: 'useAudioPlayback',
            contact: contactNameRef.current,
          });
        } else {
          logMessage('audio_play_rejected', 'error', {
            source: 'useAudioPlayback',
            errorName: err.name,
            errorMessage: err.message,
            contact: contactNameRef.current,
          });
        }

        setBlockMic(false);
        onSpeakingRef.current(null);
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
          audioUrlRef.current = null;
        }
        audioRef.current = null;
        if (onEnded) onEnded();
      });

    // --- Surface 3: synchronous throw from createAudioBlob() ---
    // Most likely cause is a malformed or empty ArrayBuffer upstream.
    // Kept separate from the other surfaces so the trace label is distinct in AppInsights.
    } catch (err) {
      logMessage('audio_blob_creation_failed', 'error', {
        source: 'useAudioPlayback',
        errorMessage: (err as Error)?.message ?? 'unknown',
        contact: contactNameRef.current,
      });

      setBlockMic(false);
      onSpeakingRef.current(null);
      audioRef.current = null;
      if (onEnded) onEnded();
    }
  };

  return { playAudio };
};